import { useEffect, useRef, useState } from 'react';

import { usePageDelimiterContentHeight } from '../contexts/page-delimitation-scale.hooks';
import {
  PAGE_CONTENT_PADDING_BOTTOM,
  PAGE_CONTENT_PADDING_TOP,
  getPageHeightInPx,
} from '../utils/page-utils';

interface PageDelimiterProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
}

interface PageBreaksState {
  breaks: number[];
  containerHeight: number;
  pageHeightPx: number;
  usablePerPage: number;
}

export default function PageDelimiter({ containerRef }: PageDelimiterProps) {
  const contentHeight = usePageDelimiterContentHeight();
  const [state, setState] = useState<PageBreaksState | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updatePageBreaks = () => {
      const containerHeight = container.scrollHeight;
      const pageHeightPx = getPageHeightInPx();

      // contentHeight is set only in edit context where box-decoration-break:clone adds
      // per-page padding. In preview/reception context it's null — no padding to subtract.
      const hasPagePadding = contentHeight != null;
      const usablePerPage = hasPagePadding
        ? pageHeightPx - PAGE_CONTENT_PADDING_TOP - PAGE_CONTENT_PADDING_BOTTOM
        : pageHeightPx;

      const breaks: number[] = [];
      let currentHeight = hasPagePadding ? PAGE_CONTENT_PADDING_TOP + usablePerPage : usablePerPage;

      const PAGE_OVERFLOW_TOLERANCE_PX = 40;
      while (currentHeight + PAGE_OVERFLOW_TOLERANCE_PX < containerHeight) {
        breaks.push(currentHeight);
        currentHeight += usablePerPage;
      }

      setState({ breaks, containerHeight, pageHeightPx, usablePerPage });
    };

    // Initial calculation with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(updatePageBreaks, 100);

    // Observe container size changes with debounce
    const debouncedUpdate = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(updatePageBreaks, 100);
    };
    observerRef.current = new ResizeObserver(debouncedUpdate);
    observerRef.current.observe(container);

    return () => {
      clearTimeout(timeoutId);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [containerRef, contentHeight]);

  if (!state || state.breaks.length === 0) {
    return null;
  }

  // When contentHeight is set (narrow viewport with spacer), only draw lines between full pages of
  // content so we don't show a line before the spacer-only "empty" page. Otherwise draw any break inside container.
  const PAGE_OVERFLOW_TOLERANCE_PX = 40;
  const breaksToShow =
    contentHeight != null
      ? state.breaks.filter(top => top + state.usablePerPage <= contentHeight)
      : state.breaks.filter(top => top + PAGE_OVERFLOW_TOLERANCE_PX < state.containerHeight);

  if (breaksToShow.length === 0) {
    return null;
  }

  // z-30: above the plain CV content, but below dialogs / sticky header (z-50) — the page
  // band must never be superposed on a modal. The scroll arrow sits at z-20, so the badge
  // still stays visible above it.
  return (
    <div className="absolute inset-0 pointer-events-none z-30 print:hidden">
      {breaksToShow.map((top, index) => (
        <div
          key={index}
          className="absolute left-0 right-0 flex items-center justify-center"
          style={{ top: `${top - 4}px`, height: '8px' }}
        >
          {/* Page separator: thin gray gap */}
          <div className="absolute inset-x-0 h-full bg-[#e0e0e0]" />
          {/* Page number badge */}
          <span className="relative z-10 bg-[#e8e8e8] text-[#666] text-[10px] font-semibold px-2 py-0.5 rounded border border-gray-300">
            Page {index + 2}
          </span>
        </div>
      ))}
    </div>
  );
}
